import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';

// Grid box colors from the app (copied from ScatteredGridBoxes.tsx)
const GRID_COLORS = [
  "#22c55e", // Green (Exercise)
  "#3b82f6", // Blue (Reading)
  "#f97316", // Orange (Meditation)
  "#ec4899", // Pink (Nutrition)
  "#8b5cf6", // Purple (default primary)
  "#06b6d4", // Cyan (Teal variant)
  "#ef4444", // Red
  "#84cc16", // Lime
];

interface GridConfettiProps {
  run: boolean;
  onConfettiComplete?: () => void;
  sourcePosition?: { x: number; y: number }; // Position where confetti should originate
}

const GridConfetti: React.FC<GridConfettiProps> = ({ run, onConfettiComplete, sourcePosition }) => {
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    handleResize(); // Set initial size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!run) {
    return null;
  }

  const drawConfettiShape = (ctx: CanvasRenderingContext2D) => {
    // Customize particles to look like small grid boxes
    const size = Math.random() * 8 + 6; // Random size between 6px and 14px
    const borderRadius = Math.max(1, Math.min(4, size * 0.15)); // Consistent with ScatteredGridBoxes

    // Rounded rectangle
    ctx.beginPath();
    ctx.moveTo(borderRadius, 0);
    ctx.lineTo(size - borderRadius, 0);
    ctx.quadraticCurveTo(size, 0, size, borderRadius);
    ctx.lineTo(size, size - borderRadius);
    ctx.quadraticCurveTo(size, size, size - borderRadius, size);
    ctx.lineTo(borderRadius, size);
    ctx.quadraticCurveTo(0, size, 0, size - borderRadius);
    ctx.lineTo(0, borderRadius);
    ctx.quadraticCurveTo(0, 0, borderRadius, 0);
    ctx.closePath();
    ctx.fill();
  };

  // Use provided source position or default to center-top
  const confettiSource = sourcePosition 
    ? { x: sourcePosition.x, y: sourcePosition.y, w: 0, h: 0 }
    : { x: windowSize.width / 2, y: windowSize.height / 3, w: 0, h: 0 };

  return (
    <Confetti
      width={windowSize.width}
      height={windowSize.height}
      numberOfPieces={30} // Reduced from 45 to 30
      recycle={false}
      gravity={0.25} // Increased gravity to make particles fall faster
      initialVelocityX={{ min: -10, max: 10 }} // Increased spread
      initialVelocityY={{ min: -15, max: -5 }} // Stronger initial upward velocity
      confettiSource={confettiSource}
      drawShape={drawConfettiShape}
      colors={GRID_COLORS}
      onConfettiComplete={onConfettiComplete}
      className="fixed top-0 left-0 w-full h-full pointer-events-none z-50"
    />
  );
};

export default GridConfetti; 